import React, { useEffect, useState } from "react"
import { Card, Button, Dropdown, Badge } from "react-bootstrap"
import { Link, useNavigate } from "react-router-dom"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import DataTable from "../../../components/DataTable/DataTable.js"
import lawyerService from "../services/lawyerService"
import provinceService from "../../province/services/provinceService.js"
import {
  Icon,
  CustomDropdownToggle,
  CustomDropdownMenu,
  LinkList,
} from "../../../components"

const LawyerList = () => {
  const navigate = useNavigate()

  const [lawyerData, setLawyer] = useState([])
  const [availableStates, setAvailableStates] = useState([])
  const [userData, setUserData] = useState([])
  const [lawyerSchoolData, setLawyerSchoolData] = useState([])
  const [provincesData, setProvincesData] = useState([])

  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)

  // Mapa de colores para cada estado (keys en minúsculas)
  const stateColorMap = {
    registrado: "blue",
    "stand by": "orange",
    "sin estado": "gray",
    activo: "green",
    inactivo: "red",
  }

  const months = [
    "enero","febrero","marzo","abril","mayo","junio",
    "julio","agosto","septiembre","octubre","noviembre","diciembre",
  ]
  const formatDate = (date) => {
    if (!date) return ""
    const [year, month, day] = date.split("-")
    return `${day} de ${months[parseInt(month, 10) - 1]} de ${year}`
  }

  const fetchLawyerList = async (pageNumber = 1, filters = {}) => {
    setLoading(true)
    try {
      const response = await lawyerService.getLawyerListPaginated(pageNumber, filters)
      setLawyer(response.results)
      setCount(response.count)
      setPage(pageNumber)
    } catch (e) {
      console.error("Error fetching lawyer list:", e)
    } finally {
      setLoading(false)
    }
  }

  const fetchStaticLists = async () => {
    try {
      const [schools, users, provinces, states] = await Promise.all([
        lawyerService.getLawyer_schoolList(),
        lawyerService.getUserList(),
        provinceService.getProvinceList(),
        lawyerService.getLawyerStateList(),
      ])
      setLawyerSchoolData(schools)
      setUserData(users)
      setProvincesData(provinces)
      setAvailableStates(states)
    } catch (e) {
      console.error("Error fetching static lists:", e)
    }
  }

  useEffect(() => {
    fetchLawyerList()
    fetchStaticLists()
  }, [])

  const handleStateChange = async (lawyerId, newStateId) => {
    setLoading(true)
    try {
      await lawyerService.updateLawyer(lawyerId, { lawyer_state: newStateId })
      setLawyer(prev =>
        prev.map(l => (l.id === lawyerId ? { ...l, lawyer_state: newStateId } : l))
      )
    } catch (e) {
      console.error("Error updating state:", e)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteLawyer = async (id) => {
    setLoading(true)
    try {
      await lawyerService.deleteLawyer(id)
      setLawyer(prev => prev.filter(l => l.id !== id))
    } catch (e) {
      console.error("Error deleting lawyer:", e)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateLawyer = () => navigate("/lawyer/new")

  const handleExport = async () => {
    setLoading(true)
    try {
      const blob = await lawyerService.getLawyerExcel({ responseType: "blob" })
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      )
      const link = document.createElement("a")
      link.href = url
      link.download = "abogados.xlsx"
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (e) {
      console.error("Export error:", e)
    } finally {
      setLoading(false)
    }
  }

  const lawyerColumns = [
    { name: "ID", selector: row => row.id, sortable: true },
    { name: "Usuario", selector: row => userData.find(u => u.id === row.user)?.name || "", sortable: true },
    { name: "Nombre", selector: row => row.name, sortable: true },
    { name: "Apellidos", selector: row => row.last_name, sortable: true },
    { name: "Empresa", selector: row => row.business_name, sortable: true },
    { name: "Teléfono", selector: row => row.phone_number, sortable: true },
    { name: "Email", selector: row => row.email, sortable: true },
    {
      name: "Provincias",
      selector: row =>
        row.service_provinces
          .map(pid => provincesData.find(p => p.id === pid)?.name || "")
          .join(", "),
      sortable: true,
    },
    { name: "Localidad", selector: row => row.locality, sortable: true },
    { name: "Dirección", selector: row => row.business_direction, sortable: true },
    {
      name: "Inicio Vacaciones",
      selector: row => (row.date_start_vacation ? formatDate(row.date_start_vacation) : "-"),
      sortable: true,
    },
    {
      name: "Fin Vacaciones",
      selector: row => (row.date_end_vacation ? formatDate(row.date_end_vacation) : "-"),
      sortable: true,
    },
    {
      name: "Colegio",
      selector: row => lawyerSchoolData.find(s => s.id === row.lawyer_school)?.name || "",
      sortable: true,
    },
    {
      name: "Código Colegio",
      sortable: true,
      // Reemplazamos `selector` por `cell` para poder aplicar estilos al contenedor
      cell: row => (
        <div style={{ paddingLeft: "100px" }}>
          {row.lawyer_school_code}
        </div>
      ),
    },
    {
      name: "Completar luego",
      sortable: true,
      cell: row => (
        <div style={{ paddingLeft: "100px" /* ajusta a tu gusto */ }}>
          {row.complete_later
            ? <Badge bg="warning">Sí</Badge>
            : <Badge bg="success">No</Badge>
          }
        </div>
      ),
    },
    {
      name: "Estado",
      headerStyle: {
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        paddingLeft: "10px",     // Mueve ligeramente el header a la derecha
      },
      width: "140px",
      grow: 0,
      cell: row => {
        const current = availableStates.find(st => st.id === row.lawyer_state)
        const nameKey = current?.name?.toLowerCase() || "sin estado"
        const bgColor = stateColorMap[nameKey] || stateColorMap["sin estado"]

        return (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end", // Empuja el select hacia la derecha
            height: "100%",
            paddingLeft: "100px",         // Espacio extra a la izquierda
          }}>
            <select
              className="form-select form-select-sm"
              style={{
                padding: "0.25rem 0.5rem",
                borderRadius: "0.25rem",
                textAlign: "center",
                backgroundColor: bgColor,
                color: "#fff",
                minWidth: "max-content",
              }}
              value={row.lawyer_state || ""}
              onChange={e => handleStateChange(row.id, +e.target.value)}
            >
              <option value="">Sin estado</option>
              {availableStates.map(st => (
                <option key={st.id} value={st.id}>
                  {st.name}
                </option>
              ))}
            </select>
          </div>
        )
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Acciones",
      cell: row => (
        <div className="text-end w-100">
          <Dropdown>
            <Dropdown.Toggle size="sm" as={CustomDropdownToggle} className="btn btn-sm btn-icon btn-zoom me-n1">
              <Icon name="more-v" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-sm" as={CustomDropdownMenu} align="end">
              <div className="dropdown-content py-1">
                <LinkList className="link-list-hover-bg-primary link-list-md">
                  <Link to={`/lawyer/${row.id}`} className="dropdown-item">
                    <Icon name="user" /><span>Perfil</span>
                  </Link>
                  <Link to={`/lawyer/edit/${row.id}`} className="dropdown-item">
                    <Icon name="edit" /><span>Editar</span>
                  </Link>
                  <button className="dropdown-item" onClick={() => handleDeleteLawyer(row.id)}>
                    <Icon name="trash" /><span>Eliminar</span>
                  </button>
                </LinkList>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
      sortable: false,
    },
  ]

  return (
    <Layout title="Abogados" content="container" loading={loading}>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Abogados</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Abogados</li>
              </ol>
            </nav>
          </Block.HeadContent>
          <Block.HeadContent>
            <ul className="d-flex gap-2">
              <li>
                <Button size="md" variant="primary" onClick={handleExport}>
                  <Icon name="plus" /><span>Exportar Abogados</span>
                </Button>
              </li>
            </ul>
          </Block.HeadContent>
          <Block.HeadContent>
            <ul className="d-flex">
              <li>
                <Button className="d-md-none" size="md" variant="primary" onClick={handleCreateLawyer}>
                  <Icon name="plus" /><span>Añadir</span>
                </Button>
              </li>
            </ul>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>
      <Block>
        <Card>
          {!loading && (
            <DataTable
              tableClassName="data-table-head-light table-responsive"
              data={lawyerData}
              columns={lawyerColumns}
              expandableRows
              actions
              searchByList={[
                "name","last_name","email","phone_number","nif","business_name"
              ]}
              backEndPagination
              fetchPaginatedData={fetchLawyerList}
              pageCount={count}
              currentPaginationPage={page}
              backendFilter
              filterFunc={fetchLawyerList}
            />
          )}
        </Card>
      </Block>
    </Layout>
  )
}

export default LawyerList
